import httpRequest from "../../ajax";
import { logout } from "../../utils/helpers";
import {
  getStoredAcquisition,
  clearStoredAcquisition,
} from "../../utils/acquisitionTracking";
import { LOGIN, ERROR, CREATE_USER, RESEND_OTP } from "../actionTypes";

/**
 * @description persist profile to store
 * @function saveToStorage
 * @param {object} data - data
 * @returns {void}
 * 
 * NOTE: Always uses localStorage to ensure authentication state is shared across all tabs.
 * This fixes the issue where clicking email links opens new tabs that can't access sessionStorage.
 */
const saveToStorage = ({ rememberMe, access_token, profile }) => {
  // Always use localStorage to ensure auth state is shared across all tabs
  localStorage.setItem("token", access_token);
  localStorage.setItem("userData", JSON.stringify(profile));
  
  if (rememberMe) {
    localStorage.setItem("rememberMe", "true");
  } else {
    localStorage.removeItem("rememberMe");
  }
};

/**
 * @description login action
 * @function loginAction
 * @param {object} requestData - request data
 * @param {function} dispatch - dispatch function
 * @returns {object} - returns the action
 */
export const loginAction = (requestData, dispatch) => {
  const { rememberMe, ...requestPayload } = requestData;
  return httpRequest("post", "/user/login", requestPayload)
    .then(({ data: { data } }) => {
      const {
        access_token,
        profile,
        account_type,
        isUserSubmittedMonthlySurvey,
        user_type,
        company,
        provider,
        provider_roles,
      } = data;

      // console.log("ID", data.id);
      profile["id"] = data.id;
      profile["account_type"] = account_type;
      profile["user_type"] = user_type;
      profile["company"] = company;
      profile["isUserSubmittedMonthlySurvey"] = isUserSubmittedMonthlySurvey;
      profile["provider"] = provider || null;
      profile["provider_roles"] = provider_roles || null;

      // TODO: check if profile.account_type == localstorage.acctType.
      // If not, kick user out. Means biz user is not logging into biz...and vice versa

      //console.log(access_token);
      saveToStorage({ rememberMe, access_token, profile, company });
      const action = { type: LOGIN, payload: profile };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const message = response?.data?.message;
      return {
        payload: message,
        type: ERROR,
      };
    });
};

/**
 * @description activate action
 * @function activateAction
 * @param {string} code - code
 * @param {function} dispatch - dispatch function
 * @returns {object} - returns the action
 */
export const activateAction = (code, dispatch) => {
  const unknownCode = "c18a62dsfsfw";
  return httpRequest("get", `/user/activate/${code}/${unknownCode}`)
    .then(({ data: { data } }) => {
      const { access_token, profile, isUserSubmittedMonthlySurvey } = data;
      profile["isUserSubmittedMonthlySurvey"] = isUserSubmittedMonthlySurvey;
      saveToStorage({
        rememberMe: !!localStorage.rememberMe,
        access_token,
        profile,
      });
      const action = { type: LOGIN, payload: profile };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const message = response?.data?.message;
      return {
        payload: message,
        type: ERROR,
      };
    });
};

/**
 * @description logout action
 * @param {function} dispatch - dispatch function
 * @returns {void}
 */
export const logoutAction = () => {
  sessionStorage.removeItem("pageToVisit");
  logout();
  window.location.reload();
};

export const createUserAction = (data, dispatch) => {
  const payload = { ...data, ...getStoredAcquisition() };

  return httpRequest("post", "/user/register", payload)
    .then(({ data: { data } }) => {
      clearStoredAcquisition();
      const action = { type: CREATE_USER, payload: data };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const message = response?.data?.message;
      return {
        payload: message,
        type: ERROR,
      };
    });
};

export const resendOTPAction = (data, dispatch) => {
  return httpRequest("post", "/user/resend/code ", data)
    .then(({ data: { data } }) => {
      const action = { type: RESEND_OTP, payload: data };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      // alert("Response: "+JSON.stringify(response))
      const message = response?.data?.message;
      return {
        payload: message,
        type: ERROR,
      };
    });
};

export const socialMediaRegister = (data, dispatch) => {
  const payload = { ...data, ...getStoredAcquisition() };

  return httpRequest("post", "/user/socialMediaRegister", payload)
    .then(({ data: { data } }) => {
      clearStoredAcquisition();
      const { access_token, profile, isUserSubmittedMonthlySurvey } = data;
      profile["isUserSubmittedMonthlySurvey"] = isUserSubmittedMonthlySurvey;
      saveToStorage({
        rememberMe: !!localStorage.rememberMe,
        access_token,
        profile,
      });
      const action = { type: LOGIN, payload: profile };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const message = response?.data?.message;
      return {
        payload: message,
        type: ERROR,
      };
    });
};

export const activateUserAction = (dispatch, activationCode) => {
  return httpRequest("get", `/user/activate/${activationCode}`)
    .then(({ data: { data } }) => {
      const {
        access_token,
        profile,
        account_type,
        isUserSubmittedMonthlySurvey,
        user_type,
        company,
      } = data;

      // console.log("ID", data.id);
      profile["id"] = data.id;
      profile["account_type"] = account_type;
      profile["user_type"] = user_type;
      profile["company"] = company;
      profile["isUserSubmittedMonthlySurvey"] = isUserSubmittedMonthlySurvey;

      saveToStorage({
        rememberMe: !!localStorage.rememberMe,
        access_token,
        profile,
        company,
      });

      const action = { type: LOGIN, payload: profile };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const message = response?.data?.message;
      return {
        payload: message,
        type: ERROR,
      };
    });
};

export const forgotPassword = (dispatch, email) => {
  return httpRequest("post", "/user/forgotPassword", email)
    .then((data) => {
      // console.log("data", data);
    })
    .catch((error) => {
      // console.log("error occ:", error);
    });
};

export const resetPassword = (dispatch, resetData) => {
  return httpRequest("post", "/user/resetPassword", resetData)
    .then((data) => {
      return data;
    })
    .catch((error) => {
      throw error;
    });
};

export const resetPasswordWithToken = (dispatch, resetData) => {
  return httpRequest("post", "/user/resetPasswordWithToken", resetData)
    .then((data) => {
      return data;
    })
    .catch((error) => {
      throw error;
    });
};
